import { useEffect, useCallback, useState } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { Paragraph } from "common/core/typography";
import { useForm } from "common/core/form";
import { Select } from "common/core/form/select";
import { TextAreaInput } from "common/core/form/text";
import LoadingIndicator from "common/core/loading_indicator";
import Button from "common/core/button";
import { LongFormattedDateTime } from "common/core/format/date";
import { DocumentNoteContext } from "graphql_globals";
import { useQuery, useMutation } from "util/graphql";

import CreateDocumentNoteMutation from "./create_document_note_mutation.graphql";
import DocumentFeedbackQuery, {
  type DocumentFeedback_document_Document as Document,
  type DocumentFeedback_document_Document_notes as DocumentNote,
} from "./document_feedback_query.graphql";
import Styles from "./index.module.scss";

type Props = {
  documentId: string;
};
type FormValues = {
  category: Category | null;
  text: string | null;
};
enum Category {
  IncorrectDisplayName = "incorrect_display_name",
  IncorrectRequirement = "incorrect_requirement",
  IncorrectDesignationLocation = "incorrect_designation_location",
  IncorrectDesignation = "incorrect_designation",
  SplitIncorrectly = "split_incorrectly",
  Other = "other",
}
type FeedbackListProps = {
  notes: DocumentNote[];
};

const CATEGORY_LABEL = {
  [Category.IncorrectDisplayName]: (
    <FormattedMessage
      id="7061c9a4-b33d-4f56-9a9d-014a14a7b37b"
      defaultMessage="Incorrect display name"
    />
  ),
  [Category.IncorrectRequirement]: (
    <FormattedMessage
      id="917fd1b4-dbd3-42d0-a76d-fef75ec25987"
      defaultMessage="Incorrect requirement (esign / wet)"
    />
  ),
  [Category.IncorrectDesignationLocation]: (
    <FormattedMessage
      id="d7c1613f-8592-4c36-acc4-78925152c1df"
      defaultMessage="Incorrect designation location(s)"
    />
  ),
  [Category.IncorrectDesignation]: (
    <FormattedMessage
      id="3ca04b46-f04d-474a-8928-56d1d9238ff6"
      defaultMessage="Incorrect or missing designation(s)"
    />
  ),
  [Category.SplitIncorrectly]: (
    <FormattedMessage
      id="3ca04b46-f04d-474a-8928-56d1d9238ff7"
      defaultMessage="Split Incorrectly"
    />
  ),
  [Category.Other]: (
    <FormattedMessage id="4132e75d-b046-4d56-b420-ae55a009c168" defaultMessage="Other" />
  ),
};

const CATEGORY_ITEMS = Object.freeze([
  {
    value: Category.IncorrectDisplayName,
    label: CATEGORY_LABEL[Category.IncorrectDisplayName],
  },
  {
    value: Category.IncorrectRequirement,
    label: CATEGORY_LABEL[Category.IncorrectRequirement],
  },
  {
    value: Category.IncorrectDesignationLocation,
    label: CATEGORY_LABEL[Category.IncorrectDesignationLocation],
  },
  {
    value: Category.IncorrectDesignation,
    label: CATEGORY_LABEL[Category.IncorrectDesignation],
  },
  {
    value: Category.SplitIncorrectly,
    label: CATEGORY_LABEL[Category.SplitIncorrectly],
  },
  {
    value: Category.Other,
    label: CATEGORY_LABEL[Category.Other],
  },
]);

const MESSAGES = defineMessages({
  commentPlaceholder: {
    id: "a8455014-de7c-4d42-97bf-c35c5637ec39",
    defaultMessage: "Comment",
  },
});

function FeedbackList(props: FeedbackListProps) {
  const { notes } = props;

  return (
    <ol className={Styles.feedbackList}>
      {notes.map((note) => (
        <li key={note.createdAt} className={Styles.item}>
          <Paragraph>
            <strong>{CATEGORY_LABEL[note.category as Category]}</strong>
          </Paragraph>
          {note.text && <Paragraph>{note.text}</Paragraph>}
          <Paragraph>{note.userName}</Paragraph>
          <Paragraph size="small" textColor="subtle">
            <LongFormattedDateTime value={note.createdAt} />
          </Paragraph>
        </li>
      ))}
    </ol>
  );
}

function Form({
  onSubmit,
  isSubmitting,
  form,
}: {
  onSubmit: (values: FormValues) => Promise<void>;
  isSubmitting: boolean;
  form: ReturnType<typeof useForm<FormValues>>;
}) {
  const intl = useIntl();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = form;

  const category = watch("category");

  return (
    <form className={Styles.documentFeedbackForm}>
      <Select
        data-automation-id="feedback-category-select"
        items={CATEGORY_ITEMS}
        placeholder={intl.formatMessage({
          id: "cd289b1e-70fb-4a42-ada6-8e1dd0f11d69",
          defaultMessage: "Add feedback",
        })}
        disabled={isSubmitting}
        aria-invalid={!!errors.category}
        {...register("category", {
          required: intl.formatMessage({
            id: "9d298aaa-c303-4737-a2f0-6021d0317b3a",
            defaultMessage: "Category is required",
          }),
        })}
      />
      {errors.category && <span className={Styles.errorMessage}>{errors.category.message}</span>}

      {category && (
        <>
          <TextAreaInput
            data-automation-id="feedback-comment"
            className="DocumentFeedback--form--text-area"
            placeholder={intl.formatMessage(MESSAGES.commentPlaceholder)}
            disabled={isSubmitting}
            aria-invalid={!!errors.text}
            {...register("text", {
              validate: (value) => {
                if (category === Category.Other) {
                  return value
                    ? true
                    : intl.formatMessage({
                        id: "cabcaeb1-e457-4ed3-820a-31c20272037d",
                        defaultMessage: "Comment is required",
                      });
                }
                return true;
              },
            })}
          />
          {errors.text && <span className={Styles.errorMessage}>{errors.text.message}</span>}

          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || isSubmitting}
            isLoading={isSubmitting}
            buttonColor="action"
            variant="primary"
          >
            <FormattedMessage id="6f8f5f43-fae9-4b6b-8ea9-7e722026a081" defaultMessage="Submit" />
          </Button>
        </>
      )}
    </form>
  );
}

function DocumentFeedbackContainer({ documentId }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, loading } = useQuery(DocumentFeedbackQuery, { variables: { documentId } });
  const createDocumentNoteMutateFn = useMutation(CreateDocumentNoteMutation);

  const form = useForm<FormValues>({
    mode: "onChange",
    defaultValues: { category: null, text: "" },
  });

  useEffect(() => {
    form.reset({ category: null, text: "" });
  }, [documentId]);

  const createDocumentNote = useCallback(
    async (values: FormValues) => {
      setIsSubmitting(true);
      try {
        await createDocumentNoteMutateFn({
          variables: {
            input: {
              documentId,
              context: DocumentNoteContext.AUTO_SPLIT_TAG,
              category: values.category!,
              text: values.text,
            },
          },
        });
        form.reset({ category: null, text: "" });
      } finally {
        setIsSubmitting(false);
      }
    },
    [documentId],
  );

  return (
    <div className={Styles.documentFeedback}>
      <Form form={form} onSubmit={createDocumentNote} isSubmitting={isSubmitting} />
      {loading ? (
        <LoadingIndicator positionRelative />
      ) : (
        <FeedbackList
          notes={(data!.document as Document).notes.filter(
            (note) => note.context === DocumentNoteContext.AUTO_SPLIT_TAG,
          )}
        />
      )}
    </div>
  );
}

export default DocumentFeedbackContainer;
